if (!window.__variantMediaSyncBound) {
  window.__variantMediaSyncBound = true;

  const SIZE_OPTION_PATTERN = /^(size|sizes|height|width|dimension|dimensions)$/i;
  const SIZE_VALUE_PATTERN = /\d/u;

  const parseConfig = (root) => {
    if (!root) return null;
    const sectionId = root.dataset.section;
    if (!sectionId) return null;
    const cfgEl = document.getElementById(`VoVariantMedia-${sectionId}`);
    if (!cfgEl) return null;
    try {
      return JSON.parse(cfgEl.textContent);
    } catch (error) {
      return null;
    }
  };

  const isSizeOptionName = (name) => SIZE_OPTION_PATTERN.test((name || '').trim());

  const isSizeValue = (value) => {
    if (!value) return false;
    return SIZE_VALUE_PATTERN.test(value) && /inch|cm|mm|size|x\d|\dx/i.test(value);
  };

  const isPlaceholderValue = (value) => {
    if (!value) return true;
    return /choose|select|pick one/i.test(value);
  };

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

  const optionIndexFromLabel = (label, names) => {
    const raw = String(label || '').replace(/:.*$/, '');
    const n = normalize(raw);
    if (!n) return -1;
    const namesList = names || [];
    let index = namesList.findIndex((name) => {
      const m = normalize(name);
      return m && (n === m || n.includes(m) || m.includes(n));
    });
    if (index >= 0) return index;
    const groups = [
      /(size|width|height|dimension)/,
      /(color|colour|frame)/,
      /(^type|type$)/
    ];
    for (const group of groups) {
      if (!group.test(n) && !group.test(raw.toLowerCase())) continue;
      index = namesList.findIndex((name) => group.test(normalize(name)) || group.test(String(name).toLowerCase()));
      if (index >= 0) return index;
    }
    return -1;
  };

  const optionKeyFromWrap = (el, data) => {
    const wrap = el && el.closest('.selector-wrapper, [data-option]');
    if (!wrap) return null;
    const explicit = wrap.getAttribute('data-option');
    if (explicit && /^option[123]$/.test(explicit)) return explicit;
    const title = wrap.querySelector('.bcpo-title');
    const label = wrap.querySelector(':scope > label');
    const text = (title && (title.dataset.voBaseLabel || title.textContent)) || (label && label.textContent) || '';
    const index = optionIndexFromLabel(text, data && data.optionNames);
    return index >= 0 ? `option${index + 1}` : null;
  };

  const selectedOptions = (root, data) => {
    const selected = {};

    root.querySelectorAll('#bcpo .bcpo-front-button-option:checked').forEach((input) => {
      const key = optionKeyFromWrap(input, data);
      if (key && input.value && !isPlaceholderValue(input.value)) selected[key] = input.value;
    });

    root.querySelectorAll('#bcpo select').forEach((select) => {
      if (select.id === 'bcpo-select') return;
      const key = optionKeyFromWrap(select, data) || select.getAttribute('data-option');
      if (key && select.value && !isPlaceholderValue(select.value)) selected[key] = select.value;
    });

    const names = (data && data.optionNames) || [];
    root.querySelectorAll('variant-selects input:checked, variant-selects select').forEach((input) => {
      const optionName = input.dataset.optionName || input.getAttribute('data-option-name');
      const value = input.tagName === 'SELECT' ? input.value : input.value;
      if (!value || isPlaceholderValue(value)) return;
      if (optionName && names.length) {
        const index = names.findIndex((name) => name === optionName);
        if (index >= 0) selected[`option${index + 1}`] = value;
      }
    });

    return selected;
  };

  const variantHasValue = (variant, value) =>
    variant.option1 === value || variant.option2 === value || variant.option3 === value;

  const matchesSelected = (variant, selected) => {
    const selectedKeys = Object.keys(selected);
    return selectedKeys.every((key) => !selected[key] || variant[key] === selected[key]);
  };

  const matchingVariant = (root, data, selected, preferredValue) => {
    const variants = data.variants || [];
    const selectedKeys = Object.keys(selected);

    if (preferredValue && !isSizeValue(preferredValue) && !isPlaceholderValue(preferredValue)) {
      const withPreferred = variants.find(
        (variant) => matchesSelected(variant, selected) && variantHasValue(variant, preferredValue)
      );
      if (withPreferred && withPreferred.media) return withPreferred;
      const byValue = variants.find((variant) => variantHasValue(variant, preferredValue) && variant.media);
      if (byValue) return byValue;
    }

    const optionNames = data.optionNames || [];
    const imageOptionKeys = optionNames
      .map((name, index) => (isSizeOptionName(name) ? null : `option${index + 1}`))
      .filter(Boolean);
    const selectedImageValues = imageOptionKeys.map((key) => selected[key]).filter(Boolean);

    if (selectedImageValues.length) {
      const byImageOption = variants.find(
        (variant) => selectedImageValues.every((value) => variantHasValue(variant, value)) && variant.media
      );
      if (byImageOption) return byImageOption;
    }

    if (selectedKeys.length) {
      const partial = variants.find((variant) => matchesSelected(variant, selected) && variant.media);
      if (partial) return partial;
    }

    const select = root.querySelector('#bcpo-select');
    const fallbackId = select && select.value ? String(select.value) : '';
    const idInput = root.querySelector('product-form input[name="id"], form input[name="id"]');
    const formId = fallbackId || (idInput && idInput.value ? String(idInput.value) : '');
    return variants.find((variant) => String(variant.id) === formId) || null;
  };

  const matchingVariantForPrice = (data, selected, preferredValue) => {
    const variants = data.variants || [];
    const selectedKeys = Object.keys(selected).filter((key) => selected[key]);

    if (selectedKeys.length) {
      const matched = variants.find((variant) => matchesSelected(variant, selected));
      if (matched) return matched;
    }

    if (preferredValue && !isPlaceholderValue(preferredValue)) {
      const byValue = variants.find((variant) => variantHasValue(variant, preferredValue));
      if (byValue) return byValue;
    }

    return null;
  };

  const updatePrice = (root, data, variant) => {
    if (!root || !data || !variant || typeof variant.price !== 'number') return;
    const priceRoot =
      root.querySelector(`#price-${data.sectionId} .price`) ||
      document.querySelector(`#price-${data.sectionId} .price`);
    if (!priceRoot) return;

    const onSale = variant.compareAtPrice > variant.price;
    priceRoot.classList.toggle('price--on-sale', onSale);

    const saleEl = priceRoot.querySelector('.price-item--sale');
    if (saleEl) saleEl.textContent = variant.priceMoney;

    priceRoot.querySelectorAll('.price-item--regular').forEach((el) => {
      if (el.closest('.price__sale')) {
        el.textContent = variant.compareAtMoney || '';
      } else {
        el.textContent = variant.priceMoney;
      }
    });

    let badge = priceRoot.querySelector('.price__badge-discount');
    const percent =
      onSale && variant.compareAtPrice
        ? Math.round(((variant.compareAtPrice - variant.price) * 100) / variant.compareAtPrice)
        : 0;

    if (onSale && percent > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'price__badge-sale price__badge-discount';
        priceRoot.appendChild(badge);
      }
      badge.textContent = `-${percent}%`;
    } else if (badge) {
      badge.remove();
    }
  };

  const activate = (root, data, variant) => {
    if (!root || !data || !variant || !variant.media) return;
    const gallery =
      root.querySelector(`#MediaGallery-${data.sectionId}`) ||
      document.querySelector(`#MediaGallery-${data.sectionId}`);
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(`${data.sectionId}-${variant.media}`, true);
    }
  };

  const clickedValue = (target) => {
    if (!target || !target.closest) return '';
    const input = target.closest('input, select');
    if (input && input.id !== 'bcpo-select' && input.value) return input.value;
    const label = target.closest('label');
    if (label) {
      const forId = label.getAttribute('for');
      const labeled = forId && document.getElementById(forId);
      if (labeled && labeled.value) return labeled.value;
    }
    return '';
  };

  const isCustomTextField = (target) =>
    target &&
    (target.matches('input[type="text"], input[type="email"], textarea') ||
      target.closest('.bcpo-text, .bcpo-textarea'));

  const sync = (target, preferredValue) => {
    const root =
      (target && target.closest && target.closest('product-info')) || document.querySelector('product-info');
    const data = parseConfig(root);
    if (!data) return;
    const selected = selectedOptions(root, data);
    activate(root, data, matchingVariant(root, data, selected, preferredValue));
    updatePrice(root, data, matchingVariantForPrice(data, selected, preferredValue));
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!target || isCustomTextField(target)) return;
      if (target.closest('#bcpo') || target.closest('variant-selects')) {
        setTimeout(() => sync(target, target.value), 0);
        setTimeout(() => sync(target, target.value), 150);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest || isCustomTextField(target)) return;
      if (
        !target.closest(
          '#bcpo .bcpo-front-button-label, #bcpo .bcpo-buttons, #bcpo select, variant-selects, .product-form__input'
        )
      ) {
        return;
      }
      const value = clickedValue(target);
      setTimeout(() => sync(target, value), 0);
      setTimeout(() => sync(target, value), 150);
    },
    true
  );
}
